// @ts-check
import objectToFormData from "object-to-formdata"

/** @typedef {object | string | number | boolean | null | undefined} CommandSubmitScalarOrObject */
/** @typedef {CommandSubmitScalarOrObject | File | CommandSubmitScalarOrObject[]} CommandSubmitValue */
/** @typedef {"json" | "raw"} SubmitTraversalType */
/**
 * @typedef {object} CommandSubmitPayload
 * @property {Record<string, CommandSubmitValue>} [global]
 * @property {import("./commands-pool.js").PoolDataType} pool
 */

/** Prepares command payloads for JSON or multipart submission. */
export default class ApiMakerCommandSubmitData {
  /**
   * @param {CommandSubmitPayload} data
   */
  constructor (data) {
    this.data = data
    this.filesCount = 0
    this.jsonData = this.traverseObject(this.data, "json")
  }

  /** @returns {number} */
  getFilesCount = () => this.filesCount

  /** @returns {Record<string, CommandSubmitValue>} */
  getJsonData = () => this.jsonData

  /**
   * Returns the raw multipart-friendly payload, generating it lazily on first access.
   * @returns {Record<string, CommandSubmitValue>}
   */
  getRawData () {
    if (!this.rawData) {
      this.rawData = this.traverseObject(this.data, "raw")
    }

    return this.rawData
  }

  /**
   * Builds a FormData payload that includes both files and serialized JSON metadata.
   * @returns {FormData}
   */
  getFormData () {
    const objectForFormData = this.getRawData() || {}

    objectForFormData.json = JSON.stringify(this.getJsonData())

    const formData = objectToFormData.serialize(objectForFormData)

    return formData
  }

  /**
   * @param {CommandSubmitValue} value
   * @param {SubmitTraversalType} type
   * @returns {CommandSubmitValue}
   */
  convertDynamic(value, type) {
    if (Array.isArray(value)) {
      return this.traverseArray(value, type)
    } else if (typeof value == "object" && value !== null && value.constructor.name == "Object") {
      return this.traverseObject(/** @type {Record<string, CommandSubmitValue>} */ (value), type)
    } else {
      return value
    }
  }

  /**
   * @param {CommandSubmitValue} object
   * @param {SubmitTraversalType} type
   * @returns {boolean}
   */
  shouldSkip(object, type) {
    if (type == "json" && object instanceof File) {
      this.filesCount += 1
      return true
    }

    if (type == "raw" && !Array.isArray(object) && !this.isObject(object) && !(object instanceof File)) {
      return true
    }

    return false
  }

  /**
   * @param {CommandSubmitValue} value
   * @returns {boolean}
   */
  isObject(value) {
    if (typeof value == "object" && value !== null && value.constructor.name == "Object") {
      return true
    }

    return false
  }

  /**
   * @param {CommandSubmitValue[]} array
   * @param {SubmitTraversalType} type
   * @returns {CommandSubmitValue[]}
   */
  traverseArray(array, type) {
    const newArray = []

    for (const value of array) {
      if (!this.shouldSkip(value, type)) {
        if (Array.isArray(value)) {
          newArray.push(this.convertDynamic(value, type))
        } else if (this.isObject(value)) {
          newArray.push(this.convertDynamic(value, type))
        } else {
          newArray.push(value)
        }
      }
    }

    return newArray
  }

  /**
   * @param {Record<string, CommandSubmitValue>} object
   * @param {SubmitTraversalType} type
   * @returns {Record<string, CommandSubmitValue>}
   */
  traverseObject(object, type) {
    const newObject = /** @type {Record<string, CommandSubmitValue>} */ ({})

    for (const key in object) {
      const value = object[key]

      if (!this.shouldSkip(value, type)) {
        if (Array.isArray(value)) {
          newObject[key] = this.convertDynamic(value, type)
        } else if (this.isObject(value)) {
          newObject[key] = this.convertDynamic(value, type)
        } else {
          newObject[key] = value
        }
      }
    }

    return newObject
  }
}
